import React, { useEffect, useState, useCallback } from "react";
import { PageLayout } from "../components/page-layout";
import { useAuth0 } from "@auth0/auth0-react";
import { useOnboarding } from "../providers/onboarding-provider";
import { Link, useNavigate } from "react-router-dom";
import { UserTypeGate } from "../components/PermissionGate";
import { 
  getVerificationProfile, 
  downloadConsentDocument,
  getPreScreenSummary,
  getOnboardingFormSummary,
  getConsentStatus,
  getUserProfile,
  downloadCareRecommendations,
  getUserSampleCollections,
  submitSampleCollection,
  getDashboardSummary
} from "../services/backend.service";
import { PageLoader } from "../components/page-loader";
import { SampleCollectionSubmission } from "../components/sample-collection-submission";
import {
  ConsentSection,
  PreScreenSection,
  VerificationSection,
  OptionalStepsSection,
  DashboardGroup,
  MemberHome,
} from "../components/dashboard";
import { getFastenConnectConfig, postFastenConnectionCallback } from "../services/fasten.service";
import analyticsService from '../services/analytics.service';
import { Button } from '../components/onyx/Button';
import { PageHeader } from '../components/onyx/PageHeader';
import { Panel } from '../components/onyx/Panel';
import { SectionHeader } from '../components/onyx/SectionHeader';
import '../components/dashboard/member-dashboard-styles';

export const DashboardPage = () => {
  const [verificationData, setVerificationData] = useState(null);
  const [preScreenData, setPreScreenData] = useState(null);
  const [updatesData, setUpdatesData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stepDataLoading, setStepDataLoading] = useState(false);
  const [consentDataLoading, setConsentDataLoading] = useState(false);
  const [onboardingDataLoading, setOnboardingDataLoading] = useState(false);
  const [profileDataLoading, setProfileDataLoading] = useState(false);
  const [sampleCollections, setSampleCollections] = useState([]);
  const [submissionDialogOpen, setSubmissionDialogOpen] = useState(false);
  const [consentData, setConsentData] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [fastenConfig, setFastenConfig] = useState(null);
  const [fastenScriptLoaded, setFastenScriptLoaded] = useState(false);
  const { getAccessTokenSilently } = useAuth0();
  const { currentStep, stepStatuses, ONBOARDING_STEPS, fetchOnboardingStatus, fastenEnabled, optionalSteps } = useOnboarding();
  
  // Check if user should connect to Fasten
  const fastenConnections = dashboardSummary?.fasten_connections || [];
  const hasConnections = fastenConnections.length > 0;
  // Show Fasten section if admin has enabled the feature for this user
  const shouldShowFastenSection = currentStep === 'DONE' && fastenConfig?.public_id && fastenEnabled;
  
  // Fetch Fasten config when needed
  useEffect(() => {
    if (currentStep === 'DONE' && !fastenConfig) {
      const fetchFastenConfig = async () => {
        try {
          console.log('🔧 Fetching Fasten config...');
          const token = await getAccessTokenSilently();
          const { data } = await getFastenConnectConfig(token);
          console.log('🔧 Fasten config loaded:', {
            public_id: data?.public_id,
            redirect_url: data?.redirect_url,
            full_config: data
          });
          setFastenConfig(data);
        } catch (err) {
          console.error('Failed to load Fasten config:', err);
        }
      };
      fetchFastenConfig();
    }
  }, [shouldShowFastenSection, fastenConfig, getAccessTokenSilently, currentStep]);

  // Debug logging for widget rendering
  useEffect(() => {
    if (shouldShowFastenSection) {
      console.log('Fasten widget state:', {
        fastenScriptLoaded,
        hasConfig: !!fastenConfig,
        hasProfileData: !!profileData,
        profileId: profileData?.id
      });
    }
  }, [shouldShowFastenSection, fastenScriptLoaded, fastenConfig, profileData]);

  // Load Fasten Stitch.js script
  useEffect(() => {
    if (!shouldShowFastenSection || fastenScriptLoaded) return;

    console.log('🎬 Loading Fasten Stitch.js script...');

    // Check if custom element is already defined
    if (customElements.get('fasten-stitch-element')) {
      console.log('✅ Fasten element already defined');
      setFastenScriptLoaded(true);
      return;
    }

    // Load CSS
    console.log('📦 Loading Fasten CSS from CDN...');
    const link = document.createElement('link');
    link.href = 'https://cdn.fastenhealth.com/connect/v3/fasten-stitch-element.css';
    link.rel = 'stylesheet';
    link.onload = () => {
      console.log('✅ Fasten CSS loaded');
      // Add custom styles AFTER Fasten CSS loads to ensure they override
      // Check if styles already exist to avoid duplicates
      if (!document.getElementById('fasten-custom-styles')) {
        const customStyle = document.createElement('style');
        customStyle.id = 'fasten-custom-styles';
        // Keep in sync with product-widgets-base.css — injected after Fasten CDN CSS.
        // Do not set min-height / stretch flex on the host; that turns the CTA into a tall slab.
        customStyle.textContent = `
          .dashboard .fasten-widget-container {
            display: flex !important;
            justify-content: center !important;
            align-items: center !important;
            min-height: 0 !important;
          }
          fasten-stitch-element {
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            width: auto !important;
            max-width: 100% !important;
            height: auto !important;
          }
          fasten-stitch-element button,
          fasten-stitch-element > button {
            background: var(--color-text, #111) !important;
            color: var(--color-bg, #fff) !important;
            border: 1px solid var(--color-text, #111) !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            padding: 1.2rem 2.4rem !important;
            font-size: var(--text-base, 1.4rem) !important;
            font-weight: 400 !important;
            letter-spacing: 0.03em !important;
            cursor: pointer !important;
            min-width: 0 !important;
            width: auto !important;
            max-width: 100% !important;
            height: auto !important;
            min-height: 0 !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            font-family: var(--font-ui, inherit) !important;
            line-height: 1.25 !important;
            box-sizing: border-box !important;
            text-decoration: none !important;
          }
          fasten-stitch-element button:hover,
          fasten-stitch-element > button:hover {
            opacity: 0.88 !important;
          }
          fasten-stitch-element button:focus,
          fasten-stitch-element > button:focus {
            outline: 2px solid var(--color-text, #111) !important;
            outline-offset: 2px !important;
            box-shadow: none !important;
          }
        `;
        document.head.appendChild(customStyle);
        console.log('Fasten custom styles injected after CSS load');
      }
    };
    document.head.appendChild(link);

    // Load JS
    console.log('📦 Loading Fasten JS from CDN...');
    const script = document.createElement('script');
    script.src = 'https://cdn.fastenhealth.com/connect/v3/fasten-stitch-element.js';
    script.type = 'module';
    script.onload = () => {
      console.log('✅ Fasten script loaded, waiting for element definition...');
      customElements.whenDefined('fasten-stitch-element').then(() => {
        console.log('✅ Fasten element defined successfully');
        setFastenScriptLoaded(true);
      });
    };
    script.onerror = (err) => {
      console.error('❌ Failed to load Fasten script:', err);
    };
    document.head.appendChild(script);
  }, [shouldShowFastenSection, fastenScriptLoaded]);

  // Handle Fasten events - supports both eventBus (v3 docs) and postMessage (actual implementation)
  useEffect(() => {
    if (!fastenScriptLoaded || !fastenConfig) {
      console.log('Fasten event listener not ready:', { fastenScriptLoaded, hasConfig: !!fastenConfig });
      return;
    }

    console.log('🎧 Setting up Fasten event listeners', {
      fastenScriptLoaded,
      hasConfig: !!fastenConfig,
      config: fastenConfig
    });

    const handleFastenEventPayload = async (eventPayload) => {
      try {
        console.log('✅ Fasten event received:', {
          event_type: eventPayload.event_type,
          api_mode: eventPayload.api_mode,
          data: eventPayload.data
        });
        
        if (eventPayload.event_type === 'patient.connection_success') {
          // Process connection immediately when it succeeds
          console.log('🔗 Patient connection successful, sending to backend:', {
            org_connection_id: eventPayload.data?.org_connection_id,
            platform_type: eventPayload.data?.platform_type,
            connection_status: eventPayload.data?.connection_status,
            full_data: eventPayload.data
          });
          
          const token = await getAccessTokenSilently();
          console.log('📤 Calling backend callback...');
          const { error, data: responseData } = await postFastenConnectionCallback(token, eventPayload.data);
          
          if (error) {
            console.error('❌ Connection callback failed:', error);
          } else {
            console.log('✅ Connection callback successful:', responseData);
            // Refresh dashboard summary
            console.log('🔄 Refreshing dashboard...');
            const { data: summaryData } = await getDashboardSummary(token);
            setDashboardSummary(summaryData);
            console.log('✅ Dashboard refreshed after connection:', {
              connections_count: summaryData?.fasten_connections?.length
            });
          }
          
        } else if (eventPayload.event_type === 'widget.complete') {
          // Widget closed - just refresh dashboard (connections already processed via patient.connection_success)
          console.log('🏁 Widget completed, refreshing dashboard');
          
          const token = await getAccessTokenSilently();
          const { data: summaryData } = await getDashboardSummary(token);
          setDashboardSummary(summaryData);
          console.log('✅ Dashboard refreshed after widget close');
          
        } else if (eventPayload.event_type === 'patient.connection_pending') {
          console.log('⏳ Patient connection pending:', eventPayload.data);
        } else if (eventPayload.event_type === 'patient.connection_failed') {
          console.error('❌ Patient connection failed:', {
            error: eventPayload.data?.error,
            error_description: eventPayload.data?.error_description,
            platform_type: eventPayload.data?.platform_type,
            full_data: eventPayload.data
          });
        } else if (eventPayload.event_type === 'widget.config_error') {
          console.error('⚠️ Widget config error:', eventPayload);
        } else if (eventPayload.event_type === 'widget.close') {
          console.log('🚪 Widget closed');
        }
      } catch (err) {
        console.error('Error handling Fasten event:', err);
      }
    };

    // NOTE: v3 docs mention eventBus, but actual implementation uses postMessage
    // Commenting out eventBus to avoid duplicate event processing
    // const element = document.querySelector('fasten-stitch-element');
    // const handleEventBus = (event) => {
    //   if (event.detail) {
    //     handleFastenEventPayload(event.detail);
    //   }
    // };
    //
    // if (element) {
    //   element.addEventListener('eventBus', handleEventBus);
    //   console.log('Fasten eventBus listener attached to element');
    // } else {
    //   console.log('Fasten element not found in DOM yet');
    // }

    // Listen for window postMessage events (v3 actual implementation)
    const handlePostMessage = (event) => {
      // Debug: Log ALL postMessages to diagnose staging issues
      console.log('🔔 postMessage received:', {
        origin: event.origin,
        dataType: typeof event.data,
        data: event.data
      });
      
      // Only process messages from Fasten origins
      if (!event.origin.includes('fastenhealth.com')) {
        console.log('❌ Rejected - origin not from fastenhealth.com');
        return;
      }

      let eventPayload = event.data;
      
      // Handle both string and object payloads
      if (typeof eventPayload === 'string') {
        try {
          eventPayload = JSON.parse(eventPayload);
        } catch (e) {
          console.log('❌ Rejected - not valid JSON:', e.message);
          return;
        }
      }
      
      // Check if it's a Fasten event
      if (eventPayload && eventPayload.event_type) {
        console.log('✅ Processing Fasten event:', eventPayload.event_type);
        handleFastenEventPayload(eventPayload);
      } else {
        console.log('❌ Rejected - no event_type in payload');
      }
    };

    window.addEventListener('message', handlePostMessage);
    console.log('✅ Fasten postMessage listener attached to window');
    console.log('🎯 Will accept postMessages from origins containing: "fastenhealth.com"');
    
    return () => {
      window.removeEventListener('message', handlePostMessage);
      console.log('🧹 Fasten postMessage listener removed');
    };
  }, [fastenScriptLoaded, fastenConfig, getAccessTokenSilently]);
  
  // Memoized function to fetch core step data (non-blocking)
  const fetchStepData = useCallback(async () => {
    if (!stepStatuses) return;

    setStepDataLoading(true);
    try {
      const token = await getAccessTokenSilently();

      if (stepStatuses?.PRE_SCREEN?.status === 'completed') {
        const preScreenResponse = await getPreScreenSummary(token);
        if (preScreenResponse?.data) {
          setPreScreenData(preScreenResponse.data);
        }
      }

      if (stepStatuses?.UPDATES?.status === 'completed') {
        const updatesResponse = await getOnboardingFormSummary(token, 'UPDATES');
        // Skipped forms still mark the step completed; summary may be empty/null.
        setUpdatesData(updatesResponse?.data || null);
      } else {
        setUpdatesData(null);
      }

      if (stepStatuses?.VERIFY?.status === 'completed') {
        const verifyResponse = await getVerificationProfile(token);
        setVerificationData(verifyResponse?.data?.verification_data || null);
      }

      if (currentStep === 'DONE') {
        const collectionsResponse = await getUserSampleCollections(token);
        const payload = collectionsResponse?.data;
        const collectionsData = Array.isArray(payload)
          ? payload
          : payload?.data;
        setSampleCollections(Array.isArray(collectionsData) ? collectionsData : []);
      }
    } catch (err) {
      console.error('Failed to load dashboard step data:', err);
      // Keep the study-home shell visible; don't replace it with a page-level error.
    } finally {
      setStepDataLoading(false);
    }
  }, [getAccessTokenSilently, stepStatuses, currentStep]);

  // Separate function to fetch consent data (non-blocking)
  const fetchConsentData = useCallback(async () => {
    if (stepStatuses?.CONSENT?.status !== 'completed') return;
    
    setConsentDataLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const { data: consentDataRaw } = await getConsentStatus(token);
      setConsentData(consentDataRaw?.consents);
    } catch (err) {
      console.error('Failed to load consent data:', err);
      // Don't set global error for consent loading failures
    } finally {
      setConsentDataLoading(false);
    }
  }, [getAccessTokenSilently, stepStatuses]);

  // Memoized function to fetch profile data (non-blocking)
  const fetchProfile = useCallback(async () => {
    if (currentStep !== 'DONE') return;
    
    setProfileDataLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const { data } = await getUserProfile(token);
      setProfileData(data);
    } catch (err) {
      console.error('Failed to load profile data:', err);
      // Don't set global error for profile loading failures
    } finally {
      setProfileDataLoading(false);
    }
  }, [getAccessTokenSilently, currentStep]);
  
  const navigate = useNavigate();

  // First effect: fetch onboarding status once on mount
  useEffect(() => {
    const fetchData = async () => {
      setOnboardingDataLoading(true);
      try {
        await fetchOnboardingStatus();
        
        // Fetch dashboard summary (includes fasten_patient data)
        try {
          const token = await getAccessTokenSilently();
          const { data: summaryData } = await getDashboardSummary(token);
          setDashboardSummary(summaryData);
        } catch (summaryErr) {
          console.error('Failed to load dashboard summary:', summaryErr);
          // Don't set global error, just log it
        }
      } catch (err) {
        setError(err);
      } finally {
        setOnboardingDataLoading(false);
      }
    };
    fetchData();
  }, [fetchOnboardingStatus, getAccessTokenSilently]);

  // Second effect: fetch step data when stepStatuses changes
  useEffect(() => {
    fetchStepData();
  }, [fetchStepData]);

  // Third effect: fetch profile data when currentStep is DONE
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Fourth effect: fetch consent data separately (non-blocking)
  useEffect(() => {
    fetchConsentData();
  }, [fetchConsentData]);

  // Main loading state (excluding consent and profile data loading to avoid blocking)
  useEffect(() => {
    if (onboardingDataLoading || stepDataLoading) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [onboardingDataLoading, stepDataLoading]);

  const handleDownload = async (docKey) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await downloadConsentDocument(token, docKey);
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `consent-${docKey}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err);
    }
  };

  const handleSampleCollectionSubmit = async (sampleCollectionId, formData) => {
    try {
      const token = await getAccessTokenSilently();
      await submitSampleCollection(token, sampleCollectionId, formData);
      
      // Refresh sample collections
      const { data: { data: collectionsData } } = await getUserSampleCollections(token);
      setSampleCollections(collectionsData || []);
      
      setSubmissionDialogOpen(false);
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const renderCareRecommendationsSection = () => {
    // Only show if user has pluto data synced
    if (profileData?.pluto_sync_status !== 'completed') {
      return null;
    }

    const handleDownloadRecommendations = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await downloadCareRecommendations(token);
        
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Get filename from Content-Disposition header
        link.download = 'Pluto Health Care Recommendations.pdf';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        setError(err);
      }
    };

    // Care recommendations section - no additional stats needed

    return (
      <Panel className="dashboard-section">
        <SectionHeader title="Pluto Health Care Recommendations" />
        <div className="onyx-detail-list">
          <div className="onyx-detail-row">
            <span className="onyx-detail-label">Care plan</span>
            <span className="onyx-detail-value">
              Onyx partners with Pluto Health (with your consent) to obtain your health records.
              As a member of Onyx, you therefore get a free subscription to Pluto Health.
              You can review the Care Recommendations they found for you in the PDF below.
            </span>
            <Button type="button" variant="primary" onClick={handleDownloadRecommendations}>
              Download Recommendations
            </Button>
          </div>
        </div>
      </Panel>
    );
  };

  // Pre-Screen / Verification / Consent sections were inlined here
  // historically — they're now reusable presentational components in
  // ``components/dashboard``. The CSC's "enrolled application" view
  // (``runner/EnrolledStepPanel``) renders the same three blocks
  // against an equivalent backend payload.
  const renderPreScreenSection = () => (
    <PreScreenSection
      data={preScreenData}
      completedAt={stepStatuses?.PRE_SCREEN?.completed_at}
    />
  );
  const renderUpdatesSection = () => {
    const updatesStep = (optionalSteps || []).find((step) => step.key === 'UPDATES');
    // Answer dump only when the member completed the form (not skip).
    if (!updatesData || Object.keys(updatesData).length === 0) {
      return null;
    }
    if (updatesStep && updatesStep.status !== 'completed') {
      return null;
    }
    const canUpdate = Boolean(updatesStep?.can_open && updatesStep?.path);
    return (
      <PreScreenSection
        data={updatesData}
        completedAt={updatesStep?.completed_at || stepStatuses?.UPDATES?.completed_at}
        title={updatesStep?.label || 'Health Updates'}
        eyebrow="Onboarding form"
        description={updatesStep?.description || null}
        action={
          canUpdate ? (
            <Button type="button" variant="ghost" component={Link} to={updatesStep.path}>
              Update answers
            </Button>
          ) : null
        }
      />
    );
  };
  const renderOptionalStepsSection = (statuses) => (
    <OptionalStepsSection steps={optionalSteps} statuses={statuses} />
  );
  const renderVerificationSection = () => (
    <VerificationSection
      data={verificationData}
      completedAt={stepStatuses?.VERIFY?.completed_at}
    />
  );
  const renderConsentSection = () => (
    <ConsentSection
      consents={consentData}
      completedAt={stepStatuses?.CONSENT?.completed_at}
      onDownload={handleDownload}
      loading={consentDataLoading || !consentData}
    />
  );

  const renderFastenWidgetOnly = () => (
    <div className="fasten-widget-container">
      {fastenScriptLoaded && fastenConfig && profileData?.id ? (
        <fasten-stitch-element
          public-id={fastenConfig.public_id}
          external-id={`user_${profileData.id}`}
          redirect-url={fastenConfig.redirect_url}
          static-backdrop="true"
          tefca-mode="false"
        >
          Connect Your Health Portal
        </fasten-stitch-element>
      ) : (
        <div className="dashboard__fasten-loading">
          <p className="dashboard__fasten-loading-text">Loading Fasten Connect...</p>
          {!fastenScriptLoaded && <p className="dashboard__fasten-loading-detail">Loading script...</p>}
          {!fastenConfig && fastenScriptLoaded && <p className="dashboard__fasten-loading-detail">Loading configuration...</p>}
          {!profileData && fastenScriptLoaded && fastenConfig && <p className="dashboard__fasten-loading-detail">Loading profile...</p>}
        </div>
      )}
    </div>
  );

  const renderFastenSection = () => {
    if (!shouldShowFastenSection) {
      return null;
    }
    return (
      <Panel className="dashboard-section">
        <SectionHeader title="Health portals" />
        <p className="content__body">
          {hasConnections
            ? 'Connect more health portals so we can get a complete picture of your medical history.'
            : 'Connect your health portals to Onyx via our partner Fasten Health. This allows us to get a copy of your medical records for the study you enrolled in!'}
        </p>
        {renderFastenWidgetOnly()}
      </Panel>
    );
  };

  const renderIneligibleSection = () => {
    return (
      <Panel className="dashboard-section" tone="error">
        <SectionHeader title="Let's Stay In Touch" />
        <div className="alert alert--error">
          <p>{'Based on your answers, you are not eligble to participate in current studies.'}</p>
          <p>We'll keep you updated on future studies and opportunities.</p>
        </div>
      </Panel>
    );
  };

  const renderApplicationRecord = () => (
    <>
      {renderUpdatesSection()}
      <OptionalStepsSection
        steps={(optionalSteps || []).filter(
          (step) => step.status === 'completed' && step.key !== 'UPDATES',
        )}
      />
      {renderPreScreenSection()}
      {renderConsentSection()}
      {renderVerificationSection()}
      {renderCareRecommendationsSection()}
    </>
  );

  const basicPageTemplate = (
    content,
    { showDefaultHeader = true, layoutClass = 'content-layout--reading' } = {},
  ) => (
    <PageLayout>
      <div className={`content-layout ${layoutClass}`}>
        <div className="dashboard">
          {showDefaultHeader ? (
            <PageHeader
              title={
                currentStep === 'DONE' && profileData?.enrollment_status !== 'provisional'
                  ? 'Your study home'
                  : 'Application status'
              }
              description={
                currentStep === 'DONE' && profileData?.enrollment_status !== 'provisional'
                  ? 'Enrollment is complete. Keep visit logistics current and finish any remaining optional forms or portal connections.'
                  : null
              }
            />
          ) : null}
          {content}
        </div>
      </div>
    </PageLayout>
  );

  if (loading) {
    return basicPageTemplate(
      <PageLoader />
    );
  };

  if (error) {
    return basicPageTemplate(
      <div className="error-message">{error.message}</div>
    );
  };

  if (currentStep === 'INELIGIBLE') {
    return basicPageTemplate(
      renderIneligibleSection()
    );
  };

  if (currentStep && currentStep !== 'DONE') {
    return basicPageTemplate(
      <>
        <Panel className="dashboard-section">
          <p className="content__body">Welcome to Onyx! Please complete our application process to enroll in the study.</p>
          <Button
            variant="primary"
            component={Link}
            to={ONBOARDING_STEPS[currentStep].path}
          >
            Enroll in Study
          </Button>
        </Panel>
        <DashboardGroup
          title="Needs attention"
          description="Optional onboarding forms you can complete now or revisit later."
          testId="dashboard-needs-attention"
        >
          {renderOptionalStepsSection(['pending', 'skipped'])}
        </DashboardGroup>
      </>
    );
  }

  if (currentStep && currentStep === 'DONE' && profileData?.enrollment_status === 'provisional') {
    return basicPageTemplate(
      <>
        <Panel className="dashboard-section" tone="error">
          <div className="success-message">
            <p>
              Thank you for joining Onyx! We are unable to access your complete health records with the information you provided.
            </p>
            <br />
            <p>
              Please <Link to="/profile" className="text-link">update your profile</Link> with additional
              phone numbers, addresses, and aliases so we can determine study eligibility.
            </p>
            <br />
            <Button type="button" variant="primary" onClick={() => navigate('/profile')}>
              Update Profile
            </Button>
          </div>
        </Panel>
        <DashboardGroup
          title="Needs attention"
          description="Optional onboarding forms and portal connections that still need a response."
          testId="dashboard-needs-attention"
        >
          {renderOptionalStepsSection(['pending', 'skipped'])}
          {renderFastenSection()}
        </DashboardGroup>
        <DashboardGroup
          title="Your information"
          description="Everything you’ve shared during enrollment."
          testId="dashboard-your-information"
        >
          {renderApplicationRecord()}
        </DashboardGroup>
      </>
    );
  }

  // Enrolled member study-home — mock layout (To do / study visit / accordion).
  return basicPageTemplate(
    <>
      <MemberHome
        profileData={profileData}
        optionalSteps={optionalSteps}
        stepStatuses={stepStatuses}
        sampleCollections={sampleCollections}
        onStartPending={() => setSubmissionDialogOpen(true)}
        updatesData={updatesData}
        preScreenData={preScreenData}
        verificationData={verificationData}
        consentData={consentData}
        onDownloadConsent={handleDownload}
        fastenEnabled={Boolean(shouldShowFastenSection)}
        fastenConnections={fastenConnections}
        fastenWidget={shouldShowFastenSection ? renderFastenWidgetOnly() : null}
      />
      <SampleCollectionSubmission
        open={submissionDialogOpen}
        onClose={() => setSubmissionDialogOpen(false)}
        sampleCollection={sampleCollections.find((c) => c.status === 'initiated')}
        onSubmit={handleSampleCollectionSubmit}
        userProfile={profileData}
      />
    </>,
    { showDefaultHeader: false, layoutClass: 'content-layout--wide' },
  );
};